import React, {FC, useRef, useState} from 'react';
import {TransformWrapper, TransformComponent, ReactZoomPanPinchRef} from 'react-zoom-pan-pinch';
import {ImageOptimized} from '@/_application/core/components/ui/image';
import {productPlaceholder} from '@/lib/placeholders';
import {FetchPriority} from '@/_application/core/types';
import {HoverZoomImage} from '../hover-zoom';
import {ResetIcon} from '@/_application/icons/reset-icon';

interface ImageSource {
    url: string;
    alt: string;
}

interface ImageSlideProps {
    variant: 'carousel' | 'thumbnail';
    imageSrc: ImageSource;
    width?: number;
    height?: number;
    zoomMode?: boolean;
    hoverZoomMode?: boolean;
    onImageClick?: () => void;
    priority?: boolean;
    fetchPriority?: FetchPriority;
}

const ImageSkeleton: FC<{width: number; height: number}> = ({width, height}) => (
    <div className="animate-pulse bg-gray-200 rounded" style={{width, height, maxWidth: '100%'}} />
);

export const ImageSlide: FC<ImageSlideProps> = ({
    variant,
    imageSrc,
    zoomMode = false,
    hoverZoomMode = false,
    onImageClick,
    width = 400,
    height = 400,
    priority = false,
    fetchPriority = FetchPriority.AUTO,
}) => {
    const transformRef = useRef<ReactZoomPanPinchRef>(null);
    const [isLoading, setIsLoading] = useState(true);
    const handleOnClick = () => {
        if (onImageClick) onImageClick();
    };
    const handleZoomIn = () => {
        transformRef.current?.zoomIn();
    };

    const handleZoomOut = () => {
        transformRef.current?.zoomOut();
    };

    const handleReset = () => {
        transformRef.current?.resetTransform();
    };

    const handleImageLoad = () => setIsLoading(false);

    if (variant === 'carousel' && zoomMode) {
        return (
            <div>
                <TransformWrapper ref={transformRef} centerOnInit wheel={{step: 50}} minScale={1} maxScale={3}>
                    <TransformComponent wrapperClass="h-full!" contentClass="w-full! h-full!">
                        {isLoading && <ImageSkeleton width={width} height={height} />}
                        <ImageOptimized
                            src={imageSrc?.url ?? productPlaceholder}
                            alt={imageSrc?.alt}
                            quality={100}
                            onClick={handleOnClick}
                            width={width}
                            height={height}
                            className={`object-contain h-[revert-layer] transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                            sizes="636px"
                            onLoad={handleImageLoad}
                        />
                    </TransformComponent>
                </TransformWrapper>
                <div className="absolute top-2 left-2 flex gap-2 flex-col">
                    <button
                        onClick={handleZoomIn}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary text-primary bg-white text-2xl">
                        +
                    </button>
                    <button
                        onClick={handleReset}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary text-primary! bg-white text-2xl">
                        <ResetIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={handleZoomOut}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary text-primary bg-white text-2xl">
                        -
                    </button>
                </div>
            </div>
        );
    }
    if (variant === 'carousel' && hoverZoomMode) {
        return (
            <HoverZoomImage
                imageSrc={imageSrc}
                width={width}
                height={height}
                priority={priority}
                fetchPriority={fetchPriority}
                onClick={handleOnClick}
            />
        );
    }
    if (variant === 'carousel') {
        return (
            <div className="relative">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <ImageSkeleton width={width} height={height} />
                    </div>
                )}
                <ImageOptimized
                    src={imageSrc?.url ?? productPlaceholder}
                    alt={imageSrc?.alt}
                    width={width}
                    height={height}
                    priority={priority}
                    fetchPriority={fetchPriority}
                    quality={100}
                    onClick={handleOnClick}
                    className={`w-full md:w-fit transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={handleImageLoad}
                />
            </div>
        );
    }
    if (variant === 'thumbnail') {
        return (
            <div className="relative h-16 w-16">
                {isLoading && (
                    <div className="absolute inset-0">
                        <ImageSkeleton width={70} height={70} />
                    </div>
                )}
                <ImageOptimized
                    src={imageSrc?.url ?? productPlaceholder}
                    alt={imageSrc?.alt}
                    width={70}
                    height={70}
                    draggable={false}
                    className={`object-contain select-none h-[revert-layer] max-h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={handleImageLoad}
                />
            </div>
        );
    }
};
